import { Injectable, inject, signal } from '@angular/core';
import { Asset } from '@app/models/asset/asset';
import { TransferUnit } from '@app/models/inventory/transfer-unit';
import { InventoryTransaction } from '@app/models/knitting/kntiting-requistion-report';
import { Roll } from '@app/models/knitting/roll';
import { Fabric, WorkOrder } from '@app/models/order/work-order';
import { ProductionCard, ProductionCardStatus } from '@app/models/production/production-card';
import { ProductionCardBundle } from '@app/models/production/production-card/production-card-bundle';
import { DyeingColor } from '@app/models/production/dyeing/dyeing-color';
import { FinishingRoute } from '@app/models/production/finishing-route';
import { AssetProxyService } from '@app/proxies/asset/asset.proxy.service';
import { LabDipRequestProxyService } from '@app/proxies/item/lab-dip-request-proxy.service';
import { WorkOrderProxyService } from '@app/proxies/order/work-order.proxy.service';
import { ProductionCardBundleProxyService } from '@app/proxies/production/production-card-bundle.proxy.service';
import { ProductionCardsProxyService } from '@app/proxies/production/production-card.proxy.service';
import { RollProxyService } from '@app/proxies/production/roll.proxy.service';
import { FinishingRouteProxyService } from '@app/proxies/production/finishing-route.proxy.service';
import { TransferUnitProxyService } from '@app/proxies/inventory/transfer-unit.proxy.service';
import { ProductionCardStatusUtil } from '@app/utilities/production-card-status.util';

import _ from 'lodash';
import { BehaviorSubject, Observable, throwError, of } from 'rxjs';
import { tap, catchError, map, filter, take, switchMap } from 'rxjs/operators';
import { ApiResponse } from '@card-software/nexus-utilities';

@Injectable({
  providedIn: 'root',
})
export class IndividualProductionCardService {
  private readonly _productionCard = signal<ProductionCard | null>(null);
  private readonly _productionCardBundles = signal<ProductionCardBundle[]>([]);
  private readonly _dyeingAsset = signal<Asset | null>(null);
  private readonly _workOrder = signal<WorkOrder | null>(null);
  private readonly _availableRolls = signal<Roll[]>([]);
  private readonly _transferUnits = signal<TransferUnit[]>([]);
  private readonly _dyeingColor = signal<DyeingColor | null>(null);
  private readonly _childrenRolls = signal<Roll[]>([]);

  private readonly _activeInventoryTransactionsInBatching = signal<InventoryTransaction[]>([]);
  private readonly _inventoryTransactionsBatchingToGreige = signal<InventoryTransaction[]>([]);
  readonly productionCard = this._productionCard.asReadonly();
  readonly activeInventoryTransactionsInBatchingSignal = this._activeInventoryTransactionsInBatching.asReadonly();
  readonly inventoryTransactionsBatchingToGreigeSignal = this._inventoryTransactionsBatchingToGreige.asReadonly();

  private _isInitialized = false;
  private readonly _isInitialized$ = new BehaviorSubject<boolean>(false);

  /**
   * Observable that emits true when the service has been initialized.
   * Components can subscribe to this to wait for initialization.
   * Only emits true values (filters out false) and completes after first emission.
   * If already initialized when subscribed, emits immediately.
   */
  readonly isInitialized$ = this._isInitialized$.asObservable().pipe(
    filter((value) => value === true),
    take(1),
  );

  private readonly productionCardProxyService = inject(ProductionCardsProxyService);
  private readonly productionCardBundlesProxyService = inject(ProductionCardBundleProxyService);
  private readonly assetProxyService = inject(AssetProxyService);
  private readonly workOrderProxyService = inject(WorkOrderProxyService);
  private readonly rollProxyService = inject(RollProxyService);
  private readonly transferUnitProxyService = inject(TransferUnitProxyService);
  private readonly finishingRouteProxyService = inject(FinishingRouteProxyService);
  private readonly labDipRequestProxyService = inject(LabDipRequestProxyService);

  //#region Important Methods and Initialization

  /**
   * Initialize the service with a production card number.
   * This must be called before using any other methods.
   * @param productionCardNumber The production card number to load
   * @returns Observable that completes when initialized or errors if not found
   */
  initialize(productionCardNumber: string): Observable<ProductionCard> {
    //check if already initialized with the same production card number
    const currentCard = this._productionCard();
    if (this._isInitialized && !_.isNil(currentCard) && currentCard.productionCardNumber === productionCardNumber) {
      return new Observable<ProductionCard>((observer) => {
        observer.next(currentCard);
        observer.complete();
      });
    }
    return this.productionCardProxyService.getByProductionCardNumber(productionCardNumber).pipe(
      map((response) => {
        if (!response.data) {
          throw new Error(`Production Card not found with number: ${productionCardNumber}`);
        }
        return response.data;
      }),
      tap((productionCard) => {
        this.resetSignals();
        this._productionCard.set(productionCard);
        this.proxyGetAssetById();
        this.proxyGetWorkOrder();
        this.proxyGetProductionCardBundles();
        this.proxyGetDyeingColor();
        this._isInitialized = true;
        this._isInitialized$.next(true);
      }),
      catchError((error) => {
        console.error('Failed to load Production Card:', error);
        this._isInitialized = false;
        this._isInitialized$.next(false);
        this._productionCard.set(null);
        return throwError(() => error);
      }),
    );
  }

  getProductionCardOrThrow(): ProductionCard {
    const card = this._productionCard();
    if (!card) {
      throw new Error('IndividualProductionCardService must be initialized before use. Call initialize() first.');
    }
    return card;
  }

  isInitialized(): boolean {
    return this._isInitialized && this._productionCard() !== null;
  }

  reset(): void {
    this._productionCard.set(null);
    this._isInitialized = false;
    this._isInitialized$.next(false);
  }

  resetSignals(): void {
    this._productionCardBundles.set([]);
    this._dyeingAsset.set(null);
    this._workOrder.set(null);
    this._availableRolls.set([]);
    this._transferUnits.set([]);
    this._dyeingColor.set(null);
    this._activeInventoryTransactionsInBatching.set([]);
  }

  refreshData(): void {
    if (!this.isInitialized()) {
      console.warn('Cannot refresh data: Service is not initialized.');
      return;
    }

    if (!_.isEmpty(this._productionCardBundles())) {
      this.proxyGetProductionCardBundles();
    }
  }

  proxyGetProductionCardBundles(): void {
    const productionCard = this.getProductionCardOrThrow();
    this.productionCardBundlesProxyService.getByProductionCardId(productionCard.id!).subscribe((response) => {
      this._productionCardBundles.set(response.data || []);
    });
  }

  proxyGetWorkOrder(): void {
    const productionCard = this.getProductionCardOrThrow();
    if (productionCard.workOrderReference) {
      this.workOrderProxyService.getById(productionCard.workOrderReference, true).subscribe((response) => {
        this._workOrder.set(response.data);
      });
    }
  }

  proxyGetAssetById(): void {
    const productionCard = this.getProductionCardOrThrow();
    if (productionCard.dyeingAssetId) {
      this.assetProxyService.getByAssetId(productionCard.dyeingAssetId).subscribe((response) => {
        this._dyeingAsset.set(response.data);
      });
    }
  }

  proxyGetDyeingColor(): void {
    const productionCard = this.getProductionCardOrThrow();
    if (productionCard.colorReference) {
      this.labDipRequestProxyService.getColorsByIds([productionCard.colorReference]).subscribe((response) => {
        if (response.data && response.data.length > 0) {
          this._dyeingColor.set(response.data[0]);
        }
      });
    }
  }

  private readonly BATCHING_LOCATION_CODE = 'NT.WA-BATCHING-1';

  proxyGetActiveInventoryTransactionsInBatching(): Observable<true> {
    const productionCard = this.getProductionCardOrThrow();

    return this.productionCardProxyService.getActiveInventoryTransactionsInBatching(productionCard.id!, true).pipe(
      switchMap((response) => {
        const transactions = response.data || [];
        this._activeInventoryTransactionsInBatching.set(transactions);

        // Combine roll numbers from ALL transactions
        const rollNumbers = transactions.flatMap((tx) => (tx.lines || []).map((line) => line.lotItemKey.number!));
        const uniqueRollNumbers = [...new Set(rollNumbers)];

        if (uniqueRollNumbers.length === 0) {
          return of(true as const);
        }

        return this.rollProxyService.getRollsByNumbers(uniqueRollNumbers).pipe(
          switchMap((rollsResponse) => {
            //push rolls to available rolls store without removing existing ones
            const existingRolls = this._availableRolls();
            const newRolls = rollsResponse.data || [];

            // Filter to only include rolls that are currently linked to this production card
            const linkedRolls = newRolls.filter((roll) => roll.currentlyLinkedToProductionCardKey?.reference === productionCard.id);

            const combinedRolls = _.uniqBy([...existingRolls, ...linkedRolls], 'number');
            this._availableRolls.set(combinedRolls);

            // Fetch children rolls only when in batching status
            if (productionCard.status === ProductionCardStatus.Batching) {
              this.proxyGetChildrenRolls().subscribe();
            }

            if (!_.isEmpty(rollsResponse.data)) {
              return this.proxyGetTransferUnits(rollsResponse.data!);
            }
            return of(true as const);
          }),
        );
      }),
    );
  }

  proxyGetInventoryTransactionsBatchingToGreige(): Observable<true> {
    const productionCard = this.getProductionCardOrThrow();

    return this.productionCardProxyService.getInventoryTransactionsBatchingToGreige(productionCard.id!, true).pipe(
      tap((response) => {
        this._inventoryTransactionsBatchingToGreige.set(response.data || []);
      }),
      map(() => true as const),
    );
  }

  proxyGetChildrenRolls(): Observable<true> {
    const productionCard = this.getProductionCardOrThrow();

    return this.productionCardProxyService.getChildrenRolls(productionCard.id!, this.BATCHING_LOCATION_CODE).pipe(
      tap((response) => {
        this._childrenRolls.set(response.data || []);
      }),
      map(() => true as const),
      catchError(() => {
        // If the call fails, just set empty array and continue
        this._childrenRolls.set([]);
        return of(true as const);
      }),
    );
  }

  /**
   * Get rolls for reprocess scenarios based on status.
   * When reprocessNumber > 0:
   * - InRouteToBatching: Use batching transaction endpoint
   * - InRouteToBrd: Use bundles (already handled via proxyGetProductionCardBundles)
   * - ReadyForFinishing: Use getRollsCurrentlyLinkedToProductionCardNumber
   */
  proxyGetRollsForReprocess(): Observable<true> {
    const productionCard = this.getProductionCardOrThrow();

    // Only applies to reprocess production cards
    if (!productionCard.reprocessNumber || productionCard.reprocessNumber <= 0) {
      return of(true as const);
    }

    // For ReadyForFinishing status, use getRollsCurrentlyLinkedToProductionCardNumber
    if (
      productionCard.status === ProductionCardStatus.ReadyForFinishing ||
      productionCard.status === ProductionCardStatus.Brd ||
      productionCard.status === ProductionCardStatus.Finishing
    ) {
      return this.rollProxyService.getRollsCurrentlyLinkedToProductionCardNumber(productionCard.productionCardNumber!).pipe(
        tap((response) => {
          const existingRolls = this._availableRolls();
          const newRolls = response.data || [];
          const combinedRolls = _.uniqBy([...existingRolls, ...newRolls], 'number');
          this._availableRolls.set(combinedRolls);
        }),
        switchMap((response) => {
          if (!_.isEmpty(response.data)) {
            return this.proxyGetTransferUnits(response.data!);
          }
          return of(true as const);
        }),
      );
    }

    // For InRouteToBatching, the batching transaction method handles it
    // For InRouteToBrd, bundles are loaded via proxyGetProductionCardBundles
    return of(true as const);
  }

  proxyGetAvailableRolls(): Observable<true> {
    const productionCard = this.getProductionCardOrThrow();

    if (productionCard.workOrderReference && ProductionCardStatusUtil.planningForBatching(productionCard.status)) {
      return this.rollProxyService.getRollsByWorkOrder(productionCard.workOrderReference, true).pipe(
        tap((response) => {
          //push rolls to available rolls store without removing existing ones
          const existingRolls = this._availableRolls();
          const newRolls = response.data || [];
          const combinedRolls = _.uniqBy([...existingRolls, ...newRolls], 'number');
          this._availableRolls.set(combinedRolls);
        }),
        switchMap((response) => {
          if (!_.isEmpty(response.data)) {
            return this.proxyGetTransferUnits(response.data!);
          }
          return of(true as const);
        }),
      );
    }
    return of(true as const);
  }

  proxyGetTransferUnits(rolls: Roll[]): Observable<true> {
    // Use highestRollNumber when available (for split rolls) to find the original Transfer Unit
    const rollNumbers = rolls.map((r) => r.highestRollNumber || r.number!);
    const uniqueRollNumbers = [...new Set(rollNumbers)];
    return this.transferUnitProxyService.getTransferUnitsForRollNumbers(uniqueRollNumbers).pipe(
      tap((response) => {
        // push transfer units witout removing existing ones and not duplicating
        const existingUnits = this._transferUnits();
        const newUnits = response.data || [];
        const combinedUnits = _.uniqBy([...existingUnits, ...newUnits], 'number');
        this._transferUnits.set(combinedUnits);
      }),
      map(() => true as const),
    );
  }

  proxyGetFinishingRoutes(): Observable<ApiResponse<FinishingRoute[]>> {
    const pcId = this.productionCard()?.id;
    if (!pcId) {
      throw new Error('Production card not initialized');
    }
    return this.finishingRouteProxyService.getByProductionCardId(pcId);
  }

  //#endregion

  //#region Getters
  get productionCardBundles(): ProductionCardBundle[] {
    return this._productionCardBundles();
  }

  get dyeingAsset(): Asset | null {
    return this._dyeingAsset();
  }

  get dyeingColor(): DyeingColor | null {
    return this._dyeingColor();
  }

  get workOrder(): WorkOrder | null {
    return this._workOrder();
  }

  get availableRolls(): Roll[] {
    return this._availableRolls();
  }

  get transferUnits(): TransferUnit[] {
    return this._transferUnits();
  }

  get activeInventoryTransactionsInBatching(): InventoryTransaction[] {
    return this._activeInventoryTransactionsInBatching();
  }

  get childrenRolls(): Roll[] {
    return this._childrenRolls();
  }

  // Expose childrenRolls as readonly signal for reactive access
  readonly childrenRollsSignal = this._childrenRolls.asReadonly();

  get fabrics(): Fabric[] {
    const fabrics = this.workOrder?.fabrics.map((fabric) => fabric.fabric) || [];
    return _.uniqBy(fabrics, 'fabricCode');
  }

  /**
   * Distinct product order numbers of every garment on the work order, as a single label.
   * 'N/A' when there is no work order loaded or none of its garments carries one.
   */
  get clientPO(): string {
    const workOrder = this.workOrder;
    if (!workOrder) return 'N/A';

    const clientPos = workOrder.fabrics
      .map((f) => f.garmetDetails?.map((g) => g.productOrderNumber))
      .flat()
      .filter((po) => po && po.trim() !== '');

    const distinctClientPos = Array.from(new Set(clientPos));

    return distinctClientPos.length > 0 ? distinctClientPos.join(', ') : 'N/A';
  }
  //#endregion

  //#region Updaters

  updateActiveInventoryTransactionsInBatching(transactions: InventoryTransaction[]): void {
    this._activeInventoryTransactionsInBatching.set(transactions);
  }

  /**
   * Updates a single transaction in the list, or adds it if not present.
   * This is used when a transaction is modified (e.g., roll added/removed).
   */
  updateSingleInventoryTransaction(transaction: InventoryTransaction): void {
    const currentTransactions = this._activeInventoryTransactionsInBatching();
    const index = currentTransactions.findIndex((tx) => tx.id === transaction.id);

    if (index !== -1) {
      // Update existing transaction
      const updatedTransactions = [...currentTransactions];
      updatedTransactions[index] = transaction;
      this._activeInventoryTransactionsInBatching.set(updatedTransactions);
    } else {
      // Add new transaction at the beginning (most recent)
      this._activeInventoryTransactionsInBatching.set([transaction, ...currentTransactions]);
    }
  }

  updateProductionCardBundle(updatedBundle: ProductionCardBundle): void {
    const bundles = this._productionCardBundles();
    const index = bundles.findIndex((b) => b.id === updatedBundle.id);
    if (index !== -1) {
      const newBundles = [...bundles];
      newBundles[index] = updatedBundle;
      this._productionCardBundles.set(newBundles);
    }
  }

  addNewBundles(bundlesToAdd: ProductionCardBundle[]): void {
    const currentBundles = this._productionCardBundles();
    this._productionCardBundles.set([...currentBundles, ...bundlesToAdd]);
  }

  addRollsToAvailableRolls(rolls: Roll[]): void {
    const existingRolls = this._availableRolls();
    const combinedRolls = _.uniqBy([...existingRolls, ...rolls], 'number');
    this._availableRolls.set(combinedRolls);
  }

  removeRollsFromAvailableRolls(rollNumbers: string[]): void {
    const currentRolls = this._availableRolls();
    const filteredRolls = currentRolls.filter((roll) => !rollNumbers.includes(roll.number!));
    this._availableRolls.set(filteredRolls);
  }

  addTransferUnitsToList(transferUnits: TransferUnit[]): void {
    const existingUnits = this._transferUnits();
    const combinedUnits = _.uniqBy([...existingUnits, ...transferUnits], 'number');
    this._transferUnits.set(combinedUnits);
  }

  //#endregion

  //#region  Actions

  changeStatus(productionCardId: string, newStatus: number): Observable<ProductionCard> {
    return this.productionCardProxyService.changeStatus(productionCardId, newStatus).pipe(
      map((response) => {
        if (!response.success) {
          throw new Error(`Failed to change status for Production Card ID: ${productionCardId}`);
        }

        const updatedCard = { ...this.getProductionCardOrThrow(), status: newStatus };
        this._productionCard.set(updatedCard);
        this.refreshData();
        return updatedCard;
      }),
      catchError((error) => {
        console.error('Failed to change Production Card status:', error);
        return throwError(() => error);
      }),
    );
  }

  changeDyeingMachine(productionCardId: string, newDyeingAssetId: string): Observable<ProductionCard> {
    return this.productionCardProxyService.changeDyeingMachine(productionCardId, newDyeingAssetId).pipe(
      map((response) => {
        if (!response.success || !response.data) {
          throw new Error(`Failed to change dyeing machine for Production Card ID: ${productionCardId}`);
        }

        this._productionCard.set(response.data);
        this.proxyGetAssetById();
        return response.data;
      }),
      catchError((error) => {
        console.error('Failed to change dyeing machine:', error);
        return throwError(() => error);
      }),
    );
  }

  //#endregion
}
